import type { ApiResponse, AuthToken, User, Store, Segment } from '@/types';

// Em produção usa a API no Workers, em dev usa mocks
const IS_DEV = import.meta.env.DEV;
const API_BASE_URL = IS_DEV ? '/api' : 'https://shelf-audit-api.shelf-audit-api.workers.dev/api';

// Mock de usuário para desenvolvimento
const MOCK_USER: User = {
    id: 'user-admin',
    email: 'admin@marilan.com.br',
    name: 'Administrador',
    role: 'manager',
    type: 'internal',
};

// Mock stores
const MOCK_STORES: Store[] = [
    { id: 'store-001', name: 'Atacadão Centro', code: 'ATC001', address: 'Av. Brasil, 1500', city: 'São Paulo', state: 'SP', region: 'Sudeste', hasBacklight: true, status: 'active' },
    { id: 'store-002', name: 'Assaí Zona Norte', code: 'ASS002', address: 'Rua das Flores, 200', city: 'São Paulo', state: 'SP', region: 'Sudeste', hasBacklight: false, status: 'active' },
    { id: 'store-003', name: 'Makro Industrial', code: 'MKR003', address: 'Rod. Anhanguera, km 15', city: 'Campinas', state: 'SP', region: 'Sudeste', hasBacklight: true, status: 'active' },
    { id: 'store-004', name: 'Atacadão Barra', code: 'ATC004', address: 'Av. das Américas, 4000', city: 'Rio de Janeiro', state: 'RJ', region: 'Sudeste', hasBacklight: true, status: 'active' },
    { id: 'store-005', name: 'Assaí Curitiba', code: 'ASS005', address: 'Rua XV de Novembro, 500', city: 'Curitiba', state: 'PR', region: 'Sul', hasBacklight: false, status: 'active' },
];

// Mock segments (15 segmentos da Spec)
const MOCK_SEGMENTS: Segment[] = [
    { id: 'seg-cream-cracker', name: 'Cream Cracker', targetShare: 15, category: 'Biscoitos Salgados' },
    { id: 'seg-cream-cracker-integral', name: 'Cream Cracker Integral', targetShare: 15, category: 'Biscoitos Salgados' },
    { id: 'seg-wafer', name: 'Wafer', targetShare: 15, category: 'Biscoitos Doces' },
    { id: 'seg-laminados', name: 'Laminados', targetShare: 15, category: 'Biscoitos Doces' },
    { id: 'seg-rosquinhas', name: 'Rosquinhas', targetShare: 15, category: 'Biscoitos Doces' },
    { id: 'seg-maria', name: 'Maria', targetShare: 20, category: 'Biscoitos Doces' },
    { id: 'seg-maizena', name: 'Maizena', targetShare: 20, category: 'Biscoitos Doces' },
    { id: 'seg-bolos-rocamboles', name: 'Bolos e Rocamboles', targetShare: 20, category: 'Bolos' },
    { id: 'seg-amanteigados', name: 'Amanteigados', targetShare: 20, category: 'Biscoitos Doces' },
    { id: 'seg-recheados', name: 'Recheados', targetShare: 20, category: 'Biscoitos Recheados' },
    { id: 'seg-torradas', name: 'Torradas', targetShare: 50, category: 'Torradas' },
    { id: 'seg-recheadinhos', name: 'Recheadinhos', targetShare: 50, category: 'Biscoitos Kids' },
    { id: 'seg-snacks', name: 'Snacks', targetShare: 50, category: 'Snacks' },
    { id: 'seg-panetones', name: 'Panetones', targetShare: 50, category: 'Sazonais' },
    { id: 'seg-colomba', name: 'Colomba Pascal', targetShare: 50, category: 'Sazonais' },
];

// Mock responses para desenvolvimento local
function getMockResponse<T>(endpoint: string, method: string, data?: unknown): ApiResponse<T> | null {
    // Login mock
    if (endpoint === '/auth/login' && method === 'POST' && data) {
        const { email, password } = data as { email: string; password: string };
        if (email === 'admin@marilan.com.br' && password === 'admin123') {
            return {
                success: true,
                data: {
                    token: 'mock-jwt-token-dev',
                    user: MOCK_USER,
                    expiresAt: Date.now() + (24 * 60 * 60 * 1000),
                } as T,
            };
        }
        return { success: false, error: 'Credenciais inválidas' };
    }

    // Auth me mock
    if (endpoint === '/auth/me' && method === 'GET') {
        const token = localStorage.getItem('auth_token');
        if (token) {
            return { success: true, data: MOCK_USER as T };
        }
        return { success: false, error: 'Não autenticado' };
    }

    // Stores mock
    if (endpoint === '/stores' && method === 'GET') {
        return { success: true, data: MOCK_STORES as T };
    }

    // Store detail mock
    if (endpoint.startsWith('/stores/') && method === 'GET') {
        const id = endpoint.replace('/stores/', '');
        const store = MOCK_STORES.find(s => s.id === id);
        if (store) {
            return { success: true, data: store as T };
        }
        return { success: false, error: 'Loja não encontrada' };
    }

    // Segments mock
    if (endpoint === '/segments' && method === 'GET') {
        return { success: true, data: MOCK_SEGMENTS as T };
    }

    // Audits mock (empty for new audits)
    if (endpoint === '/audits' && method === 'GET') {
        return { success: true, data: [] as T };
    }

    // Create audit mock
    if (endpoint === '/audits' && method === 'POST' && data) {
        const { storeId } = data as { storeId: string };
        const audit = {
            id: `audit-${Date.now()}`,
            storeId,
            userId: 'user-admin',
            auditDate: new Date().toISOString().split('T')[0],
            status: 'pending',
        };
        return { success: true, data: audit as T };
    }

    return null; // Não é mock, usar API real
}

/**
 * Cliente de API com autenticação JWT
 */
class ApiClient {
    private baseUrl: string;
    private maxRetries: number;

    constructor(baseUrl: string = API_BASE_URL, maxRetries: number = 3) {
        this.baseUrl = baseUrl;
        this.maxRetries = maxRetries;
    }

    /**
     * Obtém o token JWT do localStorage
     */
    private getToken(): string | null {
        return localStorage.getItem('auth_token');
    }

    /**
     * Define o token JWT no localStorage
     */
    setToken(token: string): void {
        localStorage.setItem('auth_token', token);
    }

    /**
     * Remove o token JWT do localStorage
     */
    clearToken(): void {
        localStorage.removeItem('auth_token');
    }

    /**
     * Faz uma requisição com retry automático
     */
    private async fetchWithRetry(
        url: string,
        options: RequestInit,
        retries: number = this.maxRetries
    ): Promise<Response> {
        try {
            const response = await fetch(url, options);

            // Se não for erro de rede, retornar resposta
            if (response.ok || response.status < 500) {
                return response;
            }

            // Retry em erros 5xx
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.fetchWithRetry(url, options, retries - 1);
            }

            return response;
        } catch (error) {
            // Retry em erros de rede
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.fetchWithRetry(url, options, retries - 1);
            }
            throw error;
        }
    }

    /**
     * Faz uma requisição HTTP
     */
    async request<T>(
        method: string,
        endpoint: string,
        data?: unknown,
        options?: RequestInit
    ): Promise<ApiResponse<T>> {
        // Em modo dev, tentar usar mock primeiro
        if (IS_DEV) {
            const mockResponse = getMockResponse<T>(endpoint, method, data);
            if (mockResponse !== null) {
                console.log('[DEV] Using mock response for:', method, endpoint);
                return mockResponse;
            }
        }

        const url = `${this.baseUrl}${endpoint}`;
        const token = this.getToken();

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options?.headers,
        };

        if (token) {
            (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        }

        const fetchOptions: RequestInit = {
            method,
            headers,
            ...options,
        };

        if (data && method !== 'GET') {
            fetchOptions.body = JSON.stringify(data);
        }

        try {
            const response = await this.fetchWithRetry(url, fetchOptions);
            const json = await response.json() as Record<string, unknown>;

            if (!response.ok) {
                return {
                    success: false,
                    error: (json.error as string) || `HTTP ${response.status}`,
                };
            }

            return json as unknown as ApiResponse<T>;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro de conexão';
            return {
                success: false,
                error: message,
            };
        }
    }

    /**
     * GET request
     */
    async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>('GET', endpoint, undefined, options);
    }

    /**
     * POST request
     */
    async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>('POST', endpoint, data, options);
    }

    /**
     * PUT request
     */
    async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>('PUT', endpoint, data, options);
    }

    /**
     * PATCH request
     */
    async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>('PATCH', endpoint, data, options);
    }

    /**
     * DELETE request
     */
    async delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>('DELETE', endpoint, undefined, options);
    }

    /**
     * Upload de arquivo
     */
    async upload<T>(endpoint: string, file: File | Blob, filename?: string, additionalData?: Record<string, string>): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;
        const token = this.getToken();

        const formData = new FormData();
        formData.append('file', file, filename || 'image.jpg');

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        const headers: HeadersInit = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: formData,
            });

            const json = await response.json() as Record<string, unknown>;

            if (!response.ok) {
                return {
                    success: false,
                    error: (json.error as string) || `HTTP ${response.status}`,
                };
            }

            return json as unknown as ApiResponse<T>;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro de upload';
            return {
                success: false,
                error: message,
            };
        }
    }
}

// Exportar instância singleton
export const api = new ApiClient();
export default api;
